import { object, string, number, boolean, date } from 'yup';
import moment from 'moment';
import { PaymentTypes } from './PriceContainer';
import { SeasonPhaseStatus } from './SeasonSubscription/SeasonSubscriptionInfo';

export const validationSchema = object().shape({
  accessFeeDescription: string().required('*Required'),
  amount: number()
    .positive('Amount must be positive number.')
    .required('*Required')
    .typeError('The amount must be a number.'),
  currency: string().required('*Required'),
  accessTypeId: string().required('*Required'),
  paymentType: string().required(),
  isFreeTrialEnabled: boolean(),
  isAdditionalFeeEnabled: boolean(),
  isGeoRestrictionEnabled: boolean(),
  isScheduleStartEnabled: boolean(),
  freeTrialDays: number().when(['isFreeTrialEnabled', 'paymentType'], {
    is: (isFreeTrialEnabled: boolean, paymentType: PaymentTypes) =>
      isFreeTrialEnabled && [PaymentTypes.Subscription, PaymentTypes.Season].includes(paymentType),
    then: number().integer('The free trial days must be a whole number').required('*Required'),
  }),
  additionalFeeAmount: number().when(['isAdditionalFeeEnabled', 'paymentType'], {
    is: (isAdditionalFeeEnabled: boolean, paymentType: PaymentTypes) =>
      isAdditionalFeeEnabled && [PaymentTypes.Subscription, PaymentTypes.Season].includes(paymentType),
    then: number().required('*Required').typeError('Additional fee amount must be a number.'),
  }),
  additionalFeeDescription: string().when(['isAdditionalFeeEnabled', 'paymentType'], {
    is: (isAdditionalFeeEnabled: boolean, paymentType: PaymentTypes) =>
      isAdditionalFeeEnabled && [PaymentTypes.Subscription, PaymentTypes.Season].includes(paymentType),
    then: string().required('*Required'),
  }),
  seasonPriceAmount: number().when(['paymentType'], {
    is: (paymentType: string) => paymentType === PaymentTypes.Season,
    then: number()
      .required('*Required')
      .positive('Amount must be positive number.')
      .typeError('The amount must be a number.'),
  }),
  startsAt: date().when(['feeId', 'paymentType'], (feeId: number, paymentType: string, schema: any) => {
    if (paymentType !== PaymentTypes.Season || feeId) return schema;
    return schema.required('*Required').min(moment().toDate(), `Start date can't be in the past.`);
  }),
  expiresAt: date().when(['startsAt', 'paymentType'], (startsAt: string, paymentType: string, schema: any) => {
    if (paymentType !== PaymentTypes.Season) return schema;
    return (
      startsAt &&
      schema
        .required('*Required')
        .min(moment(startsAt).add(1, 'minutes').toDate(), `Season end date must be after season start date.`)
    );
  }),
  seasonAnchorDate: date().when(['startsAt', 'paymentType'], (startsAt: string, paymentType: string, schema: any) => {
    if (paymentType !== PaymentTypes.Season) return schema;
    return (
      startsAt &&
      schema
        .required('*Required')
        .min(moment(startsAt).add(1, 'minutes').toDate(), `Season anchor date must be after season start date.`)
    );
  }),
});

export const seasonValidationSchema = object().shape({
  seasonPrice: number()
    .positive('Amount must be positive number.')
    .required('*Required')
    .typeError('The amount must be a number.'),
  currency: string().required('*Required'),
  currentPrice: number()
    .required('*Required')
    .positive('Amount must be positive number.')
    .typeError('The amount must be a number.'),
  startsAt: date()
    .required('*Required')
    .when(['status'], (status: string, schema: any) => {
      if (status === SeasonPhaseStatus.lockedIn) return schema;
      return schema.min(moment().toDate(), `Start date can't be in the past.`);
    }),
  expiresAt: date()
    .required('*Required')
    .when(['startsAt', 'status'], (startsAt: string, status: string, schema: any) => {
      if (status === SeasonPhaseStatus.lockedIn) return schema;
      return (
        startsAt &&
        schema.min(moment(startsAt).add(1, 'minutes').toDate(), `Season end date must be after season start date.`)
      );
    }),
  anchorDate: date()
    .required('*Required')
    .when(['startsAt', 'status'], (startsAt: string, status: string, schema: any) => {
      if (status === SeasonPhaseStatus.lockedIn) return schema;
      return (
        startsAt &&
        schema.min(moment(startsAt).add(1, 'minutes').toDate(), `Season anchor date must be after season start date.`)
      );
    }),
});
